(() => {
  const ROOT_SELECTOR = '[data-xyxx-how-it-works]';
  const COMPARISON_SELECTOR = '[data-hiw-comparison]';
  const COPY_SELECTOR = '[data-hiw-copy]';
  const MOBILE_QUERY = '(max-width: 749px)';

  const sectionInstances = new WeakMap();

  class XyxxImageComparison {
    constructor(element) {
      this.element = element;

      this.beforeClip = element.querySelector('[data-hiw-before-clip]');
      this.handle = element.querySelector('[data-hiw-handle]');

      this.videos = Array.from(
        element.querySelectorAll('video.xyxx-how-it-works__asset')
      );

      this.position = Number.parseFloat(element.dataset.position || '50');

      this.pointerId = null;
      this.dragStarted = false;
      this.isPointerActive = false;

      this.startX = 0;
      this.startY = 0;

      this.pendingClientX = null;
      this.positionFrame = null;

      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerEnd = this.onPointerEnd.bind(this);
      this.onLostPointerCapture = this.onLostPointerCapture.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onWindowBlur = this.onWindowBlur.bind(this);

      if (!this.beforeClip || !this.handle) return;

      this.bindEvents();
      this.setupResizeObserver();
      this.updateComparisonWidth();
      this.setPosition(this.position);
      this.setupVideoObserver();
    }

    bindEvents() {
      this.element.addEventListener('pointerdown', this.onPointerDown, {
        passive: false,
      });

      this.element.addEventListener(
        'lostpointercapture',
        this.onLostPointerCapture
      );

      this.handle.addEventListener('keydown', this.onKeydown);
    }

    bindDocumentEvents() {
      document.addEventListener('pointermove', this.onPointerMove, {
        passive: false,
        capture: true,
      });

      document.addEventListener('pointerup', this.onPointerEnd, {
        passive: false,
        capture: true,
      });

      document.addEventListener('pointercancel', this.onPointerEnd, {
        passive: false,
        capture: true,
      });

      window.addEventListener('blur', this.onWindowBlur);
    }

    unbindDocumentEvents() {
      document.removeEventListener('pointermove', this.onPointerMove, true);
      document.removeEventListener('pointerup', this.onPointerEnd, true);
      document.removeEventListener('pointercancel', this.onPointerEnd, true);
      window.removeEventListener('blur', this.onWindowBlur);
    }

    setupResizeObserver() {
      if (!window.ResizeObserver) return;

      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.element);
    }

    onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      this.pointerId = event.pointerId;
      this.isPointerActive = true;
      this.dragStarted = event.pointerType === 'mouse';

      this.startX = event.clientX;
      this.startY = event.clientY;
      this.pendingClientX = event.clientX;

      this.bindDocumentEvents();

      try {
        this.element.setPointerCapture?.(event.pointerId);
      } catch (error) {
      }

      if (this.dragStarted) {
        event.preventDefault();
        this.beginDrag(event.clientX);
      }
    }

    onPointerMove(event) {
      if (!this.isPointerActive) return;

      if (this.pointerId !== null && event.pointerId !== this.pointerId) {
        return;
      }

      const deltaX = event.clientX - this.startX;
      const deltaY = event.clientY - this.startY;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      if (!this.dragStarted) {
        if (absY > absX && absY > 8) {
          this.endDrag(false);
          return;
        }

        if (absX > 6 && absX >= absY) {
          this.dragStarted = true;
          this.element.classList.add('is-dragging');
        }
      }

      if (!this.dragStarted) return;

      event.preventDefault();

      this.pendingClientX = event.clientX;
      this.schedulePositionUpdate();
    }

    onPointerEnd(event) {
      if (!this.isPointerActive) return;

      if (this.pointerId !== null && event.pointerId !== this.pointerId) {
        return;
      }

      if (this.dragStarted) {
        event.preventDefault();

        if (typeof event.clientX === 'number') {
          this.pendingClientX = event.clientX;
        }

        this.flushPositionUpdate();
      }

      this.endDrag(true);
    }

    onLostPointerCapture(event) {
      if (this.pointerId !== null && event.pointerId !== this.pointerId) {
        return;
      }

      this.endDrag(false);
    }

    onWindowBlur() {
      this.endDrag(false);
    }

    beginDrag(clientX) {
      this.element.classList.add('is-dragging');
      this.setPositionFromClientX(clientX);
    }

    endDrag(shouldFlush = false) {
      if (shouldFlush) {
        this.flushPositionUpdate();
      }

      this.element.classList.remove('is-dragging');

      window.cancelAnimationFrame(this.positionFrame);

      this.positionFrame = null;
      this.pendingClientX = null;

      if (
        this.pointerId !== null &&
        this.element.hasPointerCapture?.(this.pointerId)
      ) {
        try {
          this.element.releasePointerCapture(this.pointerId);
        } catch (error) {
        }
      }

      this.unbindDocumentEvents();

      this.pointerId = null;
      this.dragStarted = false;
      this.isPointerActive = false;
    }

    schedulePositionUpdate() {
      if (this.positionFrame) return;

      this.positionFrame = window.requestAnimationFrame(() => {
        this.positionFrame = null;
        this.flushPositionUpdate();
      });
    }

    flushPositionUpdate() {
      if (this.pendingClientX === null) return;

      this.setPositionFromClientX(this.pendingClientX);
    }

    onKeydown(event) {
      let nextPosition = this.position;

      switch (event.key) {
        case 'ArrowLeft':
          nextPosition -= event.shiftKey ? 10 : 2;
          break;

        case 'ArrowRight':
          nextPosition += event.shiftKey ? 10 : 2;
          break;

        case 'Home':
          nextPosition = 0;
          break;

        case 'End':
          nextPosition = 100;
          break;

        default:
          return;
      }

      event.preventDefault();
      this.setPosition(nextPosition);
    }

    onResize() {
      this.updateComparisonWidth();
      this.setPosition(this.position);
    }

    updateComparisonWidth() {
      const width = this.element.getBoundingClientRect().width;

      this.element.style.setProperty('--hiw-comparison-width', `${width}px`);
    }

    setPositionFromClientX(clientX) {
      const bounds = this.element.getBoundingClientRect();

      if (!bounds.width) return;

      const safeClientX = Math.min(bounds.right, Math.max(bounds.left, clientX));

      const percentage = ((safeClientX - bounds.left) / bounds.width) * 100;

      this.setPosition(percentage);
    }

    setPosition(value) {
      this.position = Math.min(100, Math.max(0, value));

      this.element.style.setProperty('--hiw-position', `${this.position}%`);

      this.handle.setAttribute(
        'aria-valuenow',
        String(Math.round(this.position))
      );

      this.updateContentVisibility();
    }

updateContentVisibility() {
  const beforeBadge = this.element.querySelector(
    '.xyxx-how-it-works__badge--before'
  );

  const afterBadge = this.element.querySelector(
    '.xyxx-how-it-works__badge--after'
  );

  const handleRect = this.handle.getBoundingClientRect();
  const buffer = 10;

  const isOverlapping = (badge) => {
    if (!badge) return false;

    const badgeRect = badge.getBoundingClientRect();

    return (
      handleRect.right + buffer > badgeRect.left &&
      handleRect.left - buffer < badgeRect.right &&
      handleRect.bottom > badgeRect.top &&
      handleRect.top < badgeRect.bottom
    );
  };

  const hideBeforeBadge =
    this.position <= 8 || isOverlapping(beforeBadge);

  const hideAfterBadge =
    this.position >= 92 || isOverlapping(afterBadge);

  this.element.classList.toggle(
    'is-after-only',
    hideBeforeBadge
  );

  this.element.classList.toggle(
    'is-before-only',
    hideAfterBadge
  );
}

    setupVideoObserver() {
      if (!this.videos.length) return;

      this.videos.forEach((video) => {
        video.muted = true;
        video.defaultMuted = true;
        video.loop = true;
        video.playsInline = true;
      });

      if (!window.IntersectionObserver) {
        this.playVideos();
        return;
      }

      this.videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const shouldPlay =
              entry.isIntersecting && entry.intersectionRatio >= 0.35;

            if (shouldPlay) {
              this.playVideos();
            } else {
              this.pauseVideos();
            }
          });
        },
        {
          threshold: [0, 0.35, 0.75],
        }
      );

      this.videoObserver.observe(this.element);
    }

    playVideos() {
      this.videos.forEach((video) => {
        video.muted = true;

        const playPromise = video.play();

        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      });
    }

    pauseVideos() {
      this.videos.forEach((video) => {
        video.pause();
      });
    }

    destroy() {
      window.cancelAnimationFrame(this.positionFrame);

      this.endDrag(false);

      this.resizeObserver?.disconnect();
      this.videoObserver?.disconnect();
      this.pauseVideos();

      this.element.classList.remove('is-before-only', 'is-after-only');

      this.element.removeEventListener('pointerdown', this.onPointerDown);

      this.element.removeEventListener(
        'lostpointercapture',
        this.onLostPointerCapture
      );

      this.handle?.removeEventListener('keydown', this.onKeydown);
    }
  }

  class XyxxReadMore {
    constructor(element) {
      this.element = element;

      this.content = element.querySelector('[data-hiw-copy-content]');

      this.textElement =
        this.content?.querySelector('.metafield-rich_text_field') ||
        this.content;

      this.button = element.querySelector('[data-hiw-copy-toggle]');
      this.label = element.querySelector('[data-hiw-copy-toggle-label]');

      this.mobileMediaQuery = window.matchMedia(MOBILE_QUERY);

      this.collapsedHeight = 0;
      this.expandedHeight = 0;
      this.lastWidth = 0;
      this.measureFrame = null;

      this.onToggle = this.onToggle.bind(this);
      this.onViewportChange = this.onViewportChange.bind(this);
      this.onResizeObserved = this.onResizeObserved.bind(this);

      if (!this.content || !this.textElement || !this.button) return;

      this.bindEvents();
      this.setupResizeObserver();
      this.update();

      document.fonts?.ready.then(() => this.update()).catch(() => {});
    }

    bindEvents() {
      this.button.addEventListener('click', this.onToggle);

      if (this.mobileMediaQuery.addEventListener) {
        this.mobileMediaQuery.addEventListener('change', this.onViewportChange);
      } else {
        this.mobileMediaQuery.addListener(this.onViewportChange);
      }
    }

    setupResizeObserver() {
      if (!window.ResizeObserver) return;

      this.lastWidth = this.content.getBoundingClientRect().width;

      this.resizeObserver = new ResizeObserver(this.onResizeObserved);
      this.resizeObserver.observe(this.content);
    }

    onResizeObserved(entries) {
      const entry = entries[0];

      if (!entry) return;

      const nextWidth = entry.contentRect.width;

      if (Math.abs(nextWidth - this.lastWidth) < 1) return;

      this.lastWidth = nextWidth;
      this.update();
    }

    onViewportChange() {
      this.update();
    }

    measureHeights() {
      const textStyles = window.getComputedStyle(this.textElement);
      const fontSize = Number.parseFloat(textStyles.fontSize) || 10;
      const lineHeight =
        Number.parseFloat(textStyles.lineHeight) || fontSize * 1.28;

      this.collapsedHeight = Math.ceil(lineHeight * 2);

      this.content.style.maxHeight = 'none';

      this.expandedHeight = Math.ceil(this.content.scrollHeight);

      this.element.style.setProperty(
        '--hiw-copy-collapsed-height',
        `${this.collapsedHeight}px`
      );
    }

    onToggle() {
      if (!this.mobileMediaQuery.matches) return;

      const isExpanded = this.element.classList.contains('is-expanded');

      this.setExpanded(!isExpanded);
    }

    setExpanded(isExpanded, animate = true) {
      window.cancelAnimationFrame(this.measureFrame);

      if (isExpanded) {
        this.measureHeights();

        if (!animate) {
          this.content.style.transition = 'none';
        }

        this.content.style.maxHeight = `${this.collapsedHeight}px`;

        this.element.classList.add('is-expanded');

        this.button.setAttribute('aria-expanded', 'true');

        if (this.label) {
          this.label.textContent = 'Read less';
        }

        this.measureFrame = window.requestAnimationFrame(() => {
          this.content.style.maxHeight = `${this.expandedHeight}px`;

          if (!animate) {
            window.requestAnimationFrame(() => {
              this.content.style.removeProperty('transition');
            });
          }
        });

        return;
      }

      const currentHeight = this.content.getBoundingClientRect().height;

      if (!animate) {
        this.content.style.transition = 'none';
      }

      this.content.style.maxHeight = `${currentHeight}px`;

      this.element.classList.remove('is-expanded');

      this.button.setAttribute('aria-expanded', 'false');

      if (this.label) {
        this.label.textContent = 'Read more';
      }

      this.measureFrame = window.requestAnimationFrame(() => {
        this.content.style.maxHeight = `${this.collapsedHeight}px`;

        if (!animate) {
          window.requestAnimationFrame(() => {
            this.content.style.removeProperty('transition');
          });
        }
      });
    }

    update() {
      window.cancelAnimationFrame(this.measureFrame);

      if (!this.mobileMediaQuery.matches) {
        this.element.classList.remove('is-expanded');
        this.content.style.removeProperty('max-height');
        this.button.hidden = true;
        return;
      }

      this.measureHeights();

      const hasOverflow = this.expandedHeight > this.collapsedHeight + 1;

      this.button.hidden = !hasOverflow;

      if (!hasOverflow) {
        this.content.style.maxHeight = `${this.expandedHeight}px`;
        return;
      }

      this.setExpanded(false, false);
    }

    destroy() {
      window.cancelAnimationFrame(this.measureFrame);

      this.resizeObserver?.disconnect();

      this.button?.removeEventListener('click', this.onToggle);

      if (this.mobileMediaQuery?.removeEventListener) {
        this.mobileMediaQuery.removeEventListener(
          'change',
          this.onViewportChange
        );
      } else {
        this.mobileMediaQuery?.removeListener(this.onViewportChange);
      }
    }
  }

  function getSections(root = document) {
    const sections = [];

    if (root.matches?.(ROOT_SELECTOR)) {
      sections.push(root);
    }

    root.querySelectorAll?.(ROOT_SELECTOR).forEach((section) => {
      sections.push(section);
    });

    return sections;
  }

  function initialize(root = document) {
    getSections(root).forEach((section) => {
      if (section.dataset.hiwInitialized === 'true') return;

      section.dataset.hiwInitialized = 'true';

      const comparisons = Array.from(
        section.querySelectorAll(COMPARISON_SELECTOR)
      ).map((comparison) => new XyxxImageComparison(comparison));

      const readMore = Array.from(section.querySelectorAll(COPY_SELECTOR)).map(
        (copy) => new XyxxReadMore(copy)
      );

      sectionInstances.set(section, {
        comparisons,
        readMore,
      });
    });
  }

  function unload(target) {
    getSections(target).forEach((section) => {
      const storedInstances = sectionInstances.get(section);

      if (!storedInstances) return;

      storedInstances.comparisons.forEach((instance) => instance.destroy());
      storedInstances.readMore.forEach((instance) => instance.destroy());

      sectionInstances.delete(section);

      section.removeAttribute('data-hiw-initialized');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    unload(event.target);
  });
})();